/**
 * Bilingual translation strings for the Autopia kiosk UI.
 * Indonesian (id) is the primary locale; English (en) is secondary.
 */

export type Locale = 'id' | 'en';

export interface Translations {
  // Welcome screen
  welcome: {
    title: string;
    subtitle: string;
    scanWristband: string;
    buyTickets: string;
    tagline: string;
    /** Sub-line under the AUTOPIA wordmark on the idle hero */
    museumParks: string;
    /** Large idle CTA (uppercase styling may be applied in CSS) */
    touchToStart: string;
    /** Shown while an ORD- code from the hardware scanner is being resolved */
    processingTicket: string;
    /** Idle hint under the touch CTA about scanning a ticket QR */
    qrPrintHint: string;
    /** Small caps label on the “print ticket” primary tile */
    onlineTicketLabel: string;
    /** Subtext under print ticket headline */
    ordGrpBookingHint: string;
    /** Small caps label on the walk-in / buy ticket primary tile */
    walkInLabel: string;
    /** Subtext under buy ticket headline */
    walkInBuyHint: string;
    /** Prompt above the language toggle on the active dashboard */
    selectLanguagePrompt: string;
    /** Secondary tile: scan wristband for add-ons */
    wristbandBuyAddonsSubtitle: string;
    /** Collapse the active dashboard back to the idle “touch to start” state */
    backToMainScreen: string;
  };
  // Scan screen
  scan: {
    title: string;
    instruction: string;
    placeholder: string;
    scanning: string;
    orType: string;
    submit: string;
    /** Small header label next to the live indicator */
    headerBadge: string;
    /** Input suffix when the code looks like GRP- */
    groupBadge: string;
    /** Primary submit label for group bookings */
    submitGroup: string;
    /** Explainer card — individual ORD- */
    individualCardTitle: string;
    individualCardHint: string;
    /** Explainer card — group GRP- */
    groupCardTitle: string;
    groupCardHint: string;
    /** Line before the link to /wristband (link text uses `welcome.scanWristband`) */
    wristbandUpsell: string;
    /** `usePrintFlow` / pickup errors */
    unknownCode: string;
    orderNotFound: string;
    pickupUnavailable: string;
    printUnavailable: string;
    printFailedGeneric: string;
    // ── #461 C4: pickup confirmation + printing ──
    pickupConfirmBadge: string;
    pickupTitle: string;
    pickupOrderLabel: string;
    pickupNameLabel: string;
    pickupEntryTickets: string;
    pickupAddons: string;
    pickupAlreadyPickedUpHint: string;
    pickupPrintButton: string;
    printingGroupTitle: string;
    printingWait: string;
  };
  /** Group / pickup print result summary (`PrintResultView`) */
  printResult: {
    titleSuccess: string;
    titlePartial: string;
    titleAlreadyPrinted: string;
    alreadyPrintedBadge: string;
    timeUnknown: string;
    /** Use `{count}` for the group size */
    alreadyPrintedHint: string;
    bookingLabel: string;
    totalColumn: string;
    backHome: string;
  };
  /** ORD quick pickup success / already-printed (`QuickPickupResult`) */
  quickPickup: {
    welcomeHeading: string;
    guestNameFallback: string;
    alreadyPrintedBody: string;
    alreadyPrintedHint: string;
    printingBody: string;
    printingHint: string;
    backToMainMenu: string;
    /** Countdown line: `{lead} {seconds} {suffix}` */
    autoReturnLead: string;
    autoReturnSuffix: string;
  };
  // Balance screen
  balance: {
    title: string;
    greeting: string;
    creditBalance: string;
    tier: string;
    topUp: string;
    history: string;
    done: string;
  };
  // Top-up screen
  topUp: {
    title: string;
    selectAmount: string;
    customAmount: string;
    paymentMethod: string;
    cash: string;
    qris: string;
    edcCard: string;
    total: string;
    continue: string;
    back: string;
    /** Validation hint shown when the entered amount is below the minimum */
    minAmount: string;
    // Cash flow
    insertCash: string;
    confirmReceived: string;
    // QRIS flow
    scanQris: string;
    waitingPayment: string;
    qrisInstruction: string;
    // EDC flow
    tapCard: string;
    waitingCard: string;
  };
  // Purchase screen
  purchase: {
    title: string;
    attractionCredits: string;
    addToCart: string;
    cart: string;
    totalCredits: string;
    checkout: string;
    emptyCart: string;
    scanFirst: string;
    remove: string;
    credits: string;
  };
  // Error screen
  error: {
    title: string;
    defaultMessage: string;
    tryAgain: string;
    callStaff: string;
    callingStaff: string;
    /** React `ErrorBoundary` fallback (render crash) */
    boundaryTitle: string;
    boundaryMessage: string;
    boundaryBackHome: string;
  };
  // Success screen
  success: {
    title: string;
    receiptDetails: string;
    printReceipt: string;
    done: string;
    returningIn: string;
    seconds: string;
    /** Receipt field labels */
    transactionId: string;
    time: string;
    notes: string;
  };
  // History screen
  history: {
    title: string;
    noHistory: string;
    back: string;
    date: string;
    description: string;
    amount: string;
  };
  // Inactivity warning
  inactivity: {
    warning: string;
    countdown: string;
    stayHere: string;
    /** #461 C4: seconds unit after the countdown — id " detik" (leading space → "10 detik"), en "s". */
    secondsSuffix: string;
  };
  // Common
  common: {
    loading: string;
    back: string;
    cancel: string;
    confirm: string;
    close: string;
    currency: string;
    processing: string;
    success: string;
    failed: string;
    error: string;
    logout: string;
    /** Short label on floating home chip */
    homeLabel: string;
    /** `aria-label` for floating home */
    homeAriaLabel: string;
    /** #461 C4: Clock aria-label */
    currentTimeAriaLabel: string;
  };
  // Language toggle
  language: {
    toggle: string;
    id: string;
    en: string;
  };
  // Information kiosk
  info: {
    home: string;
    parkMap: string;
    waitTimes: string;
    showSchedule: string;
    myTickets: string;
    nowShowing: string;
    estimatedWait: string;
    noShows: string;
    creditsRemaining: string;
    minHeight: string;
    queueDepth: string;
    noWaitData: string;
    scanWristband: string;
    wristbandInfo: string;
    attractionsPurchased: string;
    reservationsToday: string;
    noReservations: string;
    backToInfo: string;
    autoReturn: string;
    refreshIn: string;
  };
  // Photo kiosk
  photo: {
    gallery: string;
    selectPhotos: string;
    cart: string;
    purchasePhotos: string;
    downloadLink: string;
    receipt: string;
    pricePerPhoto: string;
    totalPhotos: string;
    payWithCredits: string;
    payWithCard: string;
    backToGallery: string;
    noPhotos: string;
    scanWristband: string;
    selected: string;
    viewSelected: string;
    downloadSent: string;
    newTransaction: string;
    capturedAt: string;
  };
  // Tier names
  tiers: {
    BRONZE: string;
    SILVER: string;
    GOLD: string;
    PLATINUM: string;
  };
  // F&B ordering
  fnb: {
    menuTitle: string;
    /** Smaller secondary line on the welcome dashboard F&B tile */
    welcomeSubtitle: string;
    cartTitle: string;
    allergenFilter: string;
    allergenFilterActive: string;
    addToCart: string;
    viewCart: string;
    placeOrder: string;
    orderNumber: string;
    estimatedReady: string;
    /** Label appended after the minutes count, e.g. "5 minutes" */
    minutes: string;
    orderReady: string;
    soldOut: string;
    payWristband: string;
    payCard: string;
    proceedCashier: string;
    orderBeingPrepared: string;
    backToMenu: string;
    done: string;
    emptyCart: string;
    /** "Menu not available" empty state label */
    menuUnavailable: string;
    backToMenuBtn: string;
    subtotal: string;
    /** Heading for the ordered items list on the confirm screen */
    orderItems: string;
    /** "Total" label used in order summaries */
    total: string;
    allergenWarning: string;
    orderStatusTitle: string;
    enterOrderRef: string;
    checkStatus: string;
    qty: string;
    remove: string;
    selectVariant: string;
    selectModifiers: string;
    quantity: string;
    sufficient: string;
    insufficient: string;
    paymentMethod: string;
    orderPlacing: string;
    statusLookup: string;
    // ── #461 C5 ──
    itemsUnit: string;
    quantityForItem: string;
    decreaseQuantity: string;
    increaseQuantity: string;
    orderSummaryLabel: string;
    menuCategoriesLabel: string;
    cartSummaryLabel: string;
  };
  /** #461 C5: staff login screen (LoginScreen) */
  login: {
    title: string;
    submit: string;
    loggingIn: string;
    emailLabel: string;
    passwordLabel: string;
    emailPlaceholder: string;
    passwordPlaceholder: string;
    kioskOnlyError: string;
  };
  /**
   * #461 D1: locale-aware Zod validation messages. Consumed by the error map in
   * i18n/zod-i18n.ts, mirroring apps/web (#460) / apps/admin (#462).
   */
  validation: {
    required: string;
    invalidType: string;
    tooSmall: string;
    tooBig: string;
    email: string;
    invalidFormat: string;
  };
  // Walk-up ticket purchase (visitor with no wristband)
  walkUp: {
    /** WalkUpShell step-progress labels + "Langkah {step} / 3" indicator */
    stepTicket: string;
    stepAttractions: string;
    stepSummary: string;
    stepIndicator: string;
    catalogTitle: string;
    selectDate: string;
    today: string;
    tomorrow: string;
    soldOut: string;
    attractionsTitle: string;
    attractionsSubtitle: string;
    /** Short footer button on attractions step (vs. `attractionSkip` sentence) */
    skip: string;
    /** Primary continue on attractions / similar steps */
    continue: string;
    /** Attractions footer when time slots still invalid */
    selectSchedule: string;
    /** Main attractions shell title (per-instance picker) */
    attractionsPanelTitle: string;
    attractionsPanelSubtitle: string;
    /** When cart has no instances (shouldn’t happen often) */
    emptyAttractionsHint: string;
    /** e.g. "{count} tiket" */
    ticketCountLabel: string;
    attractionSkip: string;
    cartTitle: string;
    cartEmpty: string;
    subtotal: string;
    total: string;
    continuePayment: string;
    paymentTitle: string;
    methodQris: string;
    methodEdc: string;
    methodCash: string;
    qrisInstruction: string;
    qrisTitle: string;
    qrisExpiresIn: string;
    qrisLoading: string;
    edcInsertCard: string;
    edcProcessing: string;
    edcApproved: string;
    edcDeclined: string;
    cashInsertCash: string;
    paymentTimeout: string;
    paymentRetry: string;
    successTitle: string;
    printingReceipt: string;
    takeReceipt: string;
    goToGate: string;
    orderNumberLabel: string;
    bookingNumberLabel: string;
    staffAssistTitle: string;
    staffAssistMessage: string;
    networkError: string;
    // Identity collection
    identityTitle: string;
    identitySubtitle: string;
    customerName: string;
    customerEmail: string;
    continueToTickets: string;
    /** `useAttractionCatalog` global error fallback */
    catalogLoadFailed: string;
    /** `useWalkUpPayment` client strings */
    paymentFailedWithReason: string;
    paymentCancelledRetry: string;
    paymentFailedRetry: string;
    qrisSnapIncomplete: string;
    paymentProgressBadgeWaiting: string;
    paymentProgressBadgeConfirming: string;
    paymentProgressTitleWaiting: string;
    paymentProgressTitleConfirming: string;
    paymentProgressSubtitleWaiting: string;
    paymentProgressSubtitleConfirming: string;
    paymentQrisPending: string;
    paymentQrisOpenSnap: string;
    paymentQrisKeepOpen: string;
    paymentConfirmingLine: string;
    paymentConfirmingWait: string;
    /** Xendit iframe overlay */
    xenditOverlayTitle: string;
    xenditOverlayCancel: string;
    xenditOverlayLoading: string;
    xenditOverlayEmbedError: string;
    xenditOverlayOpenExternal: string;
    xenditOverlayFooter: string;
    // ── #461 C1: walk-up screens ──
    // WalkUpEntryScreen
    entryTitle: string;
    entrySubtitle: string;
    cancel: string;
    bundleBadge: string;
    viewBundleContents: string;
    bundleSavings: string;
    decrease: string;
    increase: string;
    addItem: string;
    noTicketsToday: string;
    tabIndividual: string;
    tabBundle: string;
    noIndividualTickets: string;
    noBundlesAvailable: string;
    // WalkUpOverviewScreen
    selectPaymentMethod: string;
    selectEdcBank: string;
    payAmount: string;
    viaEdc: string;
    viaQris: string;
    viaCash: string;
    startOrder: string;
    overviewTitle: string;
    overviewSubtitleAction: string;
    attractionCountLabel: string;
    totalPayment: string;
    vatIncluded: string;
    paymentMethodTitle: string;
    paymentFailed: string;
    orderSummary: string;
    edit: string;
    // WalkUpSuccessScreen
    visitDateLabel: string;
    ticketsLabel: string;
    ticketsReady: string;
    preparing: string;
    wristbandsHeading: string;
    wristbandN: string;
    nameLabel: string;
    printerUnavailableShowQr: string;
    printFailedShowQr: string;
    qrWristbandAlt: string;
    takeWristbandsEnjoy: string;
    preparingWristbands: string;
    retryPrint: string;
    ticketsProcessingShowReceipt: string;
    preparingTickets: string;
    backToHome: string;
    // ── #461 C2: walk-up components ──
    ticketNumberLabel: string;
    needsScheduleTag: string;
    bundleIncludes: string;
    /** Heading above the attractions this bundle gives THIS visitor. */
    bundleAttractionsForVisitor: string;
    /** Sub-label on a bundle-included attraction row. */
    bundleIncluded: string;
    /** Sub-label when the bundle gives this visitor several redemptions. */
    bundleRedemptions: string;
    selectAddonAttractions: string;
    applyToAll: string;
    noAttractionsAvailable: string;
    noSlotsAvailable: string;
    limited: string;
    addAttraction: string;
    attractionSelected: string;
    requiredTag: string;
    slotSelectedTag: string;
    removeTicket: string;
    cashSubtitle: string;
  };
  /** Wristband profile & purchase entry (`/wristband/profile`) */
  wristband: {
    rescan: string;
    connectedBadge: string;
    yourRides: string;
    /** `{count}` = number of entitlements */
    registeredCount: string;
    loadingEntitlements: string;
    noEntitlementsTitle: string;
    noEntitlementsHint: string;
    whatToBuy: string;
    addonEyebrow: string;
    addonTitle: string;
    addonDescription: string;
    // Transfer tile
    transferEyebrow: string;
    transferTileTitle: string;
    transferTileDescription: string;
    // Transfer — select screen
    transferSelectTitle: string;
    transferLoading: string;
    transferLoadFailed: string;
    transferRetry: string;
    transferNoneTitle: string;
    transferNoneHint: string;
    /** `{count}` rides selected */
    transferSelectedCount: string;
    transferNext: string;
    // Transfer — recipient scan screen
    transferScanTitle: string;
    transferScanSubtitle: string;
    transferResolving: string;
    transferRecipientFoundTitle: string;
    transferContinueWithRecipient: string;
    transferScanAnother: string;
    transferErrorTitle: string;
    // Transfer — confirm screen
    transferConfirmTitle: string;
    transferFrom: string;
    transferTo: string;
    transferItemsLabel: string;
    transferWarnOnce: string;
    transferSubmit: string;
    transferSending: string;
    // Transfer — error messages
    transferFailed: string;
    transferCannotSendToSelf: string;
    transferDifferentOrder: string;
    transferRecipientNotFound: string;
    transferNoCustomer: string;
    transferTicketsUnavailable: string;
    transferInvalidCode: string;
    // Transfer — success screen
    transferSuccessHeadline: string;
    /** `{name}` = recipient name */
    transferSuccessMessage: string;
    transferNumberLabel: string;
    /** `{count}` tickets transferred */
    transferItemsCount: string;
    ctaStart: string;
    footerHint: string;
    /** Addon picker / success (`/wristband/addon`, …) */
    backToProfile: string;
    /** Button label without leading arrow */
    backToWristbandProfile: string;
    fnbEyebrow: string;
    fnbDescription: string;
    addonPickerBadge: string;
    addonPickerTitle: string;
    addonPickerForWristband: string;
    visitLabel: string;
    loadAttractionsFailed: string;
    noAttractionsAvailableTitle: string;
    noAttractionsAvailableHint: string;
    /** `{count}` = number of slots */
    slotsAvailable: string;
    /** `{count}` selected in card header */
    selectedCountShort: string;
    noSlotsToday: string;
    limitedSlot: string;
    /** Footer under total: `{count}` rides in cart */
    ridesSelectedCount: string;
    continueToPayment: string;
    /** Success screen */
    addonSuccessHeadline: string;
    /** `{name}` visitor (use guestFallback if empty) */
    addonSuccessEnjoy: string;
    /** `{wristbandId}` */
    addonSuccessRecorded: string;
    guestFallback: string;
    addonTransactionLabel: string;
    /** `useWristbandEntitlements` when API returns no message */
    entitlementsLoadFailed: string;
    // ── #461 C3: wristband screens + components ──
    scanServiceEyebrow: string;
    scanEyebrow: string;
    scanTitle: string;
    scanSubtitle: string;
    scanProcessing: string;
    scanInputLabel: string;
    scanInputPlaceholder: string;
    scanNotRecognizedTitle: string;
    scanNotRecognizedError: string;
    scanNoWristbandPrompt: string;
    scanBuyNewTicket: string;
    paymentTimeoutError: string;
    paymentFailedWithReason: string;
    paymentCancelledError: string;
    paymentFailedRetry: string;
    qrisConfigIncomplete: string;
    waitingPaymentStatus: string;
    processingStatus: string;
    verifyingPayment: string;
    continuePaymentInWindow: string;
    doNotClosePage: string;
    changeSelection: string;
    paymentSummary: string;
    totalPayment: string;
    ridesCountLabel: string;
    selectedRides: string;
    slotTimePrice: string;
    removeFromCart: string;
    paymentFailedTitle: string;
    total: string;
    payAmount: string;
    viaQris: string;
    greetingEyebrow: string;
    greetingHello: string;
    statusActive: string;
    entryTicketHeader: string;
    entryCategoryFallback: string;
    visitDateLabel: string;
    entitlementStatusActive: string;
    entitlementStatusUsed: string;
    entitlementStatusExpired: string;
    attractionFallback: string;
    usedAtLine: string;
    expiredAtLine: string;
    validUntilLine: string;
  };
  /** Staff reprint modal (`StaffReprintModal`) */
  staffReprint: {
    accessBadge: string;
    title: string;
    reasonLabel: string;
    reasonLost: string;
    reasonDamaged: string;
    reasonNotReceived: string;
    reasonPrintFailure: string;
    reasonOther: string;
    submitProcessing: string;
    submitButton: string;
    pinErrorGeneric: string;
    keypadDelete: string;
    keypadClear: string;
    /** Aria for numeric keys; `{digit}` = 0–9 */
    keypadDigit: string;
  };
}

export const translations: Record<Locale, Translations> = {
  id: {
    welcome: {
      title: 'Selamat Datang di',
      subtitle: 'Autopia Theme Park',
      scanWristband: 'Pindai Gelang',
      buyTickets: 'Beli Tiket',
      tagline: 'Sentuh untuk memulai',
      museumParks: 'Museum & Parks',
      touchToStart: 'Sentuh untuk Memulai',
      processingTicket: 'Memproses tiket…',
      qrPrintHint: 'Pindai QR tiket untuk langsung cetak gelang',
      onlineTicketLabel: 'Tiket Online',
      ordGrpBookingHint: 'Sudah punya booking ORD- atau GRP-?',
      walkInLabel: 'Walk-In',
      walkInBuyHint: 'Pilih tiket & wahana di sini',
      selectLanguagePrompt: 'Silakan pilih bahasa Anda',
      wristbandBuyAddonsSubtitle: 'Beli wahana & addon',
      backToMainScreen: '← Kembali ke layar utama',
    },
    scan: {
      title: 'Cetak Tiket Online Anda',
      instruction: 'Pindai kode booking Anda, atau ketik kode ORD-… / GRP-… di bawah',
      placeholder: 'Kode booking (ORD-… atau GRP-…)',
      scanning: 'Memproses...',
      orType: 'atau ketik kode',
      submit: 'Cetak Tiket',
      headerBadge: 'Cetak Tiket Online',
      groupBadge: 'Grup',
      submitGroup: 'Cetak Wristband Grup',
      individualCardTitle: 'Individu (ORD-)',
      individualCardHint: 'Cetak gelang untuk pesanan online Anda',
      groupCardTitle: 'Grup (GRP-)',
      groupCardHint: 'Cetak semua gelang anggota grup sekaligus',
      wristbandUpsell:
        'Sudah memiliki gelang dan ingin membeli wahana atau makanan?',
      unknownCode:
        'Kode tidak dikenali. Halaman ini hanya untuk cetak tiket online. Untuk pindai gelang, kembali ke beranda dan pilih "Pindai Gelang".',
      orderNotFound:
        'Nomor pesanan tidak ditemukan. Pastikan ejaan kode benar atau hubungi staf.',
      pickupUnavailable: 'Layanan pengambilan tiket sedang tidak tersedia',
      printUnavailable: 'Layanan cetak tidak tersedia',
      printFailedGeneric: 'Cetak gagal',
      // ── #461 C4: pickup confirmation + printing ──
      pickupConfirmBadge: 'Konfirmasi Pesanan',
      pickupTitle: 'Cetak Gelang Anda',
      pickupOrderLabel: 'Pesanan',
      pickupNameLabel: 'Nama',
      pickupEntryTickets: 'Tiket Masuk',
      pickupAddons: 'Wahana Tambahan',
      pickupAlreadyPickedUpHint: 'Pesanan ini sudah diambil. Hubungi staf jika perlu cetak ulang.',
      pickupPrintButton: 'Cetak {count} Gelang',
      printingGroupTitle: 'Mencetak wristband grup',
      printingWait: 'Mohon tunggu sebentar...',
    },
    printResult: {
      titleSuccess: 'Wristband Dicetak!',
      titlePartial: 'Sebagian Gagal Dicetak',
      titleAlreadyPrinted: 'Sudah Dicetak Sebelumnya',
      alreadyPrintedBadge: 'Dicetak Sebelumnya',
      timeUnknown: 'Waktu tidak diketahui',
      alreadyPrintedHint:
        '{count} wristband untuk grup ini sudah dicetak. Hubungi staf jika perlu cetak ulang.',
      bookingLabel: 'Booking',
      totalColumn: 'Total',
      backHome: 'Kembali ke Beranda',
    },
    quickPickup: {
      welcomeHeading: 'Selamat Datang!',
      guestNameFallback: 'Tamu',
      alreadyPrintedBody: 'Gelang Anda sudah dicetak sebelumnya.',
      alreadyPrintedHint: 'Hubungi staf jika ada masalah.',
      printingBody: 'Gelang Anda sedang dicetak.',
      printingHint: 'Mohon tunggu sebentar.',
      backToMainMenu: 'Kembali ke Menu Utama',
      autoReturnLead: 'Kembali otomatis dalam',
      autoReturnSuffix: 'detik…',
    },
    balance: {
      title: 'Informasi Akun',
      greeting: 'Halo',
      creditBalance: 'Saldo Kredit',
      tier: 'Tier',
      topUp: 'Isi Kredit',
      history: 'Cek Riwayat',
      done: 'Selesai',
    },
    topUp: {
      title: 'Isi Kredit',
      selectAmount: 'Pilih Jumlah',
      customAmount: 'Jumlah Lain',
      paymentMethod: 'Metode Pembayaran',
      cash: 'Tunai',
      qris: 'QRIS',
      edcCard: 'Kartu EDC',
      total: 'Total',
      continue: 'Lanjut',
      back: 'Kembali',
      minAmount: 'Jumlah minimum top up adalah Rp 10.000',
      insertCash: 'Silakan masukkan uang tunai ke mesin',
      confirmReceived: 'Konfirmasi Pembayaran Diterima',
      scanQris: 'Pindai kode QR untuk membayar',
      waitingPayment: 'Menunggu pembayaran...',
      qrisInstruction: 'Buka aplikasi dompet digital Anda dan pindai kode QR di atas',
      tapCard: 'Silakan tempel atau masukkan kartu Anda',
      waitingCard: 'Menunggu kartu...',
    },
    purchase: {
      title: 'Beli Tiket Wahana',
      attractionCredits: 'Tiket Wahana (Kredit)',
      addToCart: 'Tambah',
      cart: 'Keranjang',
      totalCredits: 'Total Kredit',
      checkout: 'Bayar Sekarang',
      emptyCart: 'Keranjang kosong',
      scanFirst: 'Pindai gelang dulu untuk checkout',
      remove: 'Hapus',
      credits: 'kredit',
    },
    error: {
      title: 'Terjadi Kesalahan',
      defaultMessage: 'Maaf, terjadi gangguan. Silakan coba lagi atau hubungi petugas.',
      tryAgain: 'Coba Lagi',
      callStaff: 'Panggil Petugas',
      callingStaff: 'Memanggil petugas...',
      boundaryTitle: 'Maaf, terjadi kesalahan',
      boundaryMessage:
        'Sistem kios mengalami gangguan sementara. Silakan kembali ke layar utama atau hubungi staf jika masalah berlanjut.',
      boundaryBackHome: 'Kembali ke Beranda',
    },
    success: {
      title: 'Berhasil!',
      receiptDetails: 'Detail Transaksi',
      printReceipt: 'Cetak Struk',
      done: 'Selesai',
      returningIn: 'Kembali ke halaman utama dalam',
      seconds: 'detik',
      transactionId: 'ID Transaksi',
      time: 'Waktu',
      notes: 'Keterangan',
    },
    history: {
      title: 'Riwayat Transaksi',
      noHistory: 'Belum ada riwayat transaksi',
      back: 'Kembali',
      date: 'Tanggal',
      description: 'Keterangan',
      amount: 'Jumlah',
    },
    inactivity: {
      warning: 'Apakah Anda masih di sini?',
      countdown: 'Sesi akan berakhir dalam',
      stayHere: 'Ya, Saya Masih Di Sini',
      secondsSuffix: ' detik', // leading space intentional → "10 detik"
    },
    common: {
      loading: 'Memuat...',
      back: 'Kembali',
      cancel: 'Batal',
      confirm: 'Konfirmasi',
      close: 'Tutup',
      currency: 'Rp',
      processing: 'Memproses...',
      success: 'Berhasil',
      failed: 'Gagal',
      error: 'Terjadi kesalahan saat memuat data',
      logout: 'Keluar',
      homeLabel: 'Beranda',
      homeAriaLabel: 'Kembali ke beranda',
      currentTimeAriaLabel: 'Waktu saat ini',
    },
    language: {
      toggle: 'EN',
      id: '🇮🇩 Indonesia',
      en: '🇬🇧 English',
    },
    tiers: {
      BRONZE: 'Perunggu',
      SILVER: 'Perak',
      GOLD: 'Emas',
      PLATINUM: 'Platinum',
    },
    info: {
      home: 'Informasi Taman',
      parkMap: 'Peta Taman',
      waitTimes: 'Waktu Tunggu',
      showSchedule: 'Jadwal Pertunjukan',
      myTickets: 'Tiket Saya',
      nowShowing: 'Sedang Berlangsung',
      estimatedWait: 'Estimasi Tunggu',
      noShows: 'Tidak ada pertunjukan hari ini',
      creditsRemaining: 'Kredit Tersisa',
      minHeight: 'Tinggi min.',
      queueDepth: 'Antrean',
      noWaitData: 'Data tidak tersedia',
      scanWristband: 'Pindai gelang untuk melihat tiket Anda',
      wristbandInfo: 'Informasi Gelang',
      attractionsPurchased: 'Wahana yang Dibeli',
      reservationsToday: 'Reservasi Hari Ini',
      noReservations: 'Tidak ada reservasi hari ini',
      backToInfo: 'Kembali ke Info',
      autoReturn: 'Kembali otomatis dalam',
      refreshIn: 'Perbarui dalam',
    },
    photo: {
      gallery: 'Galeri Foto',
      selectPhotos: 'Pilih Foto',
      cart: 'Keranjang Foto',
      purchasePhotos: 'Beli Foto',
      downloadLink: 'Link Unduhan',
      receipt: 'Bukti Pembelian',
      pricePerPhoto: 'Harga per foto',
      totalPhotos: 'Total foto',
      payWithCredits: 'Bayar dengan Kredit Gelang',
      payWithCard: 'Bayar dengan Kartu',
      backToGallery: 'Kembali ke Galeri',
      noPhotos: 'Belum ada foto hari ini',
      scanWristband: 'Pindai gelang untuk melihat foto Anda',
      selected: 'dipilih',
      viewSelected: 'Lihat Yang Dipilih ({count})',
      downloadSent: 'Link unduhan dikirim ke email/WhatsApp Anda',
      newTransaction: 'Transaksi Baru',
      capturedAt: 'Diambil pukul',
    },
    fnb: {
      menuTitle: 'Makanan & Minuman',
      welcomeSubtitle: 'Makanan & minuman',
      cartTitle: 'Keranjang',
      allergenFilter: 'Filter Alergen',
      allergenFilterActive: 'Filter Aktif',
      addToCart: 'Tambah ke Keranjang',
      viewCart: 'Lihat Keranjang',
      placeOrder: 'Pesan Sekarang',
      orderNumber: 'Nomor Pesanan',
      estimatedReady: 'Estimasi siap',
      minutes: 'menit',
      orderReady: 'Makanan Anda Siap!',
      soldOut: 'Habis',
      payWristband: 'Bayar dengan Gelang',
      payCard: 'Kartu / E-Wallet',
      proceedCashier: 'Teruskan ke Kasir',
      orderBeingPrepared: 'Pesanan Anda sedang diproses',
      backToMenu: 'Kembali ke Menu',
      done: 'Selesai',
      emptyCart: 'Keranjang masih kosong',
      menuUnavailable: 'Menu tidak tersedia',
      backToMenuBtn: 'Kembali ke Menu',
      subtotal: 'Subtotal',
      orderItems: 'Pesanan',
      total: 'Total',
      allergenWarning: 'Peringatan: Pesanan mengandung alergen yang Anda pilih',
      orderStatusTitle: 'Status Pesanan',
      enterOrderRef: 'Masukkan nomor pesanan',
      checkStatus: 'Cek Status',
      qty: 'Jml',
      remove: 'Hapus',
      selectVariant: 'Pilih Varian',
      selectModifiers: 'Tambahan (opsional)',
      quantity: 'Jumlah',
      sufficient: 'Saldo cukup',
      insufficient: 'Saldo tidak cukup',
      paymentMethod: 'Metode Pembayaran',
      orderPlacing: 'Memproses pesanan...',
      statusLookup: 'Cek Status Pesanan',
      // ── #461 C5 ──
      itemsUnit: 'item',
      quantityForItem: 'Jumlah untuk {name}',
      decreaseQuantity: 'Kurangi jumlah {name}',
      increaseQuantity: 'Tambah jumlah {name}',
      orderSummaryLabel: 'Ringkasan pesanan',
      menuCategoriesLabel: 'Kategori menu',
      cartSummaryLabel: 'Ringkasan keranjang',
    },
    login: {
      title: 'Masuk',
      submit: 'Masuk',
      loggingIn: 'Sedang masuk...',
      emailLabel: 'Email',
      passwordLabel: 'Kata Sandi',
      emailPlaceholder: 'Masukkan email Anda',
      passwordPlaceholder: 'Masukkan kata sandi Anda',
      kioskOnlyError: 'Aplikasi ini hanya untuk akun kiosk. Gunakan akun dengan role KIOSK.',
    },
    validation: {
      required: 'Bidang ini wajib diisi',
      invalidType: 'Nilai tidak valid',
      tooSmall: 'Nilai terlalu kecil',
      tooBig: 'Nilai terlalu besar',
      email: 'Masukkan alamat email yang valid',
      invalidFormat: 'Format tidak valid',
    },
    walkUp: {
      stepTicket: 'Tiket Masuk',
      stepAttractions: 'Wahana',
      stepSummary: 'Ringkasan',
      stepIndicator: 'Langkah {step} / 3',
      catalogTitle: 'Pilih Tiket',
      selectDate: 'Pilih Tanggal Kunjungan',
      today: 'Hari Ini',
      tomorrow: 'Besok',
      soldOut: 'Habis Terjual',
      attractionsTitle: 'Tambah Tiket Wahana',
      attractionsSubtitle: 'Opsional — lewati jika tidak ingin menambahkan wahana',
      skip: 'Lewati',
      continue: 'Lanjutkan',
      selectSchedule: 'Pilih jadwal',
      attractionsPanelTitle: 'Tambah Wahana',
      attractionsPanelSubtitle: 'Pilih wahana untuk setiap tiket Anda',
      emptyAttractionsHint: 'Silakan pilih tiket masuk terlebih dahulu',
      ticketCountLabel: '{count} tiket',
      attractionSkip: 'Lewati, lanjut ke keranjang',
      cartTitle: 'Keranjang Anda',
      cartEmpty: 'Keranjang Anda kosong',
      subtotal: 'Subtotal',
      total: 'Total',
      continuePayment: 'Lanjut ke Pembayaran',
      paymentTitle: 'Pilih Metode Pembayaran',
      methodQris: 'QRIS',
      methodEdc: 'Kartu (EDC)',
      methodCash: 'Tunai',
      qrisInstruction: 'Pindai kode QR menggunakan aplikasi pembayaran Anda',
      qrisTitle: 'Pindai QR dengan e-wallet Anda',
      qrisExpiresIn: 'Kedaluwarsa dalam',
      qrisLoading: 'Memuat QR…',
      edcInsertCard: 'Masukkan atau tempelkan kartu pada mesin EDC',
      edcProcessing: 'Memproses pembayaran...',
      edcApproved: 'Pembayaran Disetujui',
      edcDeclined: 'Pembayaran Ditolak',
      cashInsertCash: 'Masukkan uang tunai ke dalam mesin',
      paymentTimeout: 'Waktu pembayaran habis',
      paymentRetry: 'Coba Lagi',
      successTitle: 'Pembayaran Berhasil',
      printingReceipt: 'Mencetak struk...',
      takeReceipt: 'Silakan ambil struk Anda',
      goToGate: 'Menuju Gerbang Masuk untuk Aktivasi Gelang',
      orderNumberLabel: 'Nomor Pesanan',
      bookingNumberLabel: 'Nomor Booking',
      staffAssistTitle: 'Silakan Panggil Staf',
      staffAssistMessage: 'Tunjukkan nomor transaksi di bawah ini kepada staf',
      networkError: 'Koneksi terputus. Silakan coba lagi.',
      // Identity collection
      identityTitle: 'Data Pemesan',
      identitySubtitle: 'Masukkan nama dan email untuk menerima e-tiket Anda',
      customerName: 'Nama Lengkap',
      customerEmail: 'Email',
      continueToTickets: 'Lanjut Pilih Tiket',
      catalogLoadFailed: 'Gagal memuat wahana',
      paymentFailedWithReason: 'Pembayaran gagal. ({reason})',
      paymentCancelledRetry: 'Pembayaran dibatalkan. Silakan coba lagi.',
      paymentFailedRetry: 'Pembayaran gagal. Silakan coba lagi.',
      qrisSnapIncomplete: 'Konfigurasi pembayaran QRIS tidak lengkap.',
      paymentProgressBadgeWaiting: 'Menunggu Pembayaran',
      paymentProgressBadgeConfirming: 'Memproses Pembayaran',
      paymentProgressTitleWaiting: 'Selesaikan Pembayaran',
      paymentProgressTitleConfirming: 'Memproses Pembayaran',
      paymentProgressSubtitleWaiting:
        'Ikuti instruksi pada layar untuk menyelesaikan transaksi',
      paymentProgressSubtitleConfirming:
        'Mohon tunggu — transaksi sedang diverifikasi',
      paymentQrisPending: 'Pembayaran terdeteksi, memverifikasi transaksi...',
      paymentQrisOpenSnap: 'Lanjutkan pembayaran di jendela Midtrans yang terbuka',
      paymentQrisKeepOpen:
        'Jangan tutup halaman ini sampai transaksi selesai diverifikasi.',
      paymentConfirmingLine: 'Memproses pembayaran',
      paymentConfirmingWait: 'Transaksi sedang diverifikasi, mohon tunggu...',
      xenditOverlayTitle: 'Pembayaran Aman',
      xenditOverlayCancel: 'Batalkan',
      xenditOverlayLoading: 'Memuat halaman pembayaran...',
      xenditOverlayEmbedError: 'Halaman pembayaran tidak dapat dimuat di sini',
      xenditOverlayOpenExternal: 'Buka Halaman Pembayaran',
      xenditOverlayFooter: 'Selesaikan pembayaran di sini. Modal akan menutup otomatis setelah pembayaran dikonfirmasi.',
      // ── #461 C1: walk-up screens ──
      entryTitle: 'Pilih Tiket Masuk',
      entrySubtitle: 'Pilih jenis tiket masuk atau paket yang diinginkan',
      cancel: 'Batal',
      bundleBadge: 'Paket',
      viewBundleContents: 'Lihat isi paket',
      bundleSavings: 'Hemat {percent}%',
      decrease: 'Kurangi',
      increase: 'Tambah',
      addItem: 'Tambah {name}',
      noTicketsToday: 'Tidak ada tiket tersedia untuk hari ini',
      tabIndividual: 'Tiket Individu',
      tabBundle: 'Paket Bundle',
      noIndividualTickets: 'Tidak ada tiket individu tersedia',
      noBundlesAvailable: 'Tidak ada paket tersedia',
      selectPaymentMethod: 'Pilih metode pembayaran',
      selectEdcBank: 'Pilih Bank EDC',
      payAmount: 'Bayar {amount}',
      viaEdc: 'via EDC · {bank}',
      viaQris: 'via QRIS',
      viaCash: 'via Tunai',
      startOrder: 'Mulai pesan tiket',
      overviewTitle: 'Ringkasan & Pembayaran',
      overviewSubtitleAction: 'Pilih metode pembayaran untuk menyelesaikan',
      attractionCountLabel: '{count} wahana',
      totalPayment: 'Total Pembayaran',
      vatIncluded: 'PPN 11% termasuk',
      paymentMethodTitle: 'Metode Pembayaran',
      paymentFailed: 'Pembayaran Gagal',
      orderSummary: 'Ringkasan Pesanan',
      edit: 'Ubah',
      visitDateLabel: 'Tanggal Kunjungan',
      ticketsLabel: 'Tiket',
      ticketsReady: '{count} tiket siap',
      preparing: 'Menyiapkan…',
      wristbandsHeading: 'Gelang Anda',
      wristbandN: 'Gelang {n}',
      nameLabel: 'Nama',
      printerUnavailableShowQr: 'Printer tidak tersedia — tunjukkan QR ini di gerbang',
      printFailedShowQr: 'Cetak gagal — tunjukkan QR ini di gerbang',
      qrWristbandAlt: 'QR gelang {code}',
      takeWristbandsEnjoy: 'Ambil gelang Anda dan nikmati taman!',
      preparingWristbands: 'Menyiapkan gelang…',
      retryPrint: 'Coba Cetak Ulang',
      ticketsProcessingShowReceipt: 'Tiket sedang diproses. Tunjukkan struk kepada petugas di gerbang.',
      preparingTickets: 'Menyiapkan tiket…',
      backToHome: 'Kembali ke Beranda',
      // ── #461 C2: walk-up components ──
      ticketNumberLabel: 'Tiket #{number}',
      needsScheduleTag: 'Butuh Jadwal',
      bundleIncludes: 'Paket Termasuk',
      bundleAttractionsForVisitor: 'Wahana Dari Paket — Pilih Jadwal',
      bundleIncluded: 'Sudah termasuk paket',
      bundleRedemptions: 'Sudah termasuk paket · {count}× main',
      selectAddonAttractions: 'Pilih Wahana Tambahan',
      applyToAll: 'Terapkan ke semua',
      noAttractionsAvailable: 'Tidak ada wahana tersedia',
      noSlotsAvailable: 'Tidak ada jadwal tersedia',
      limited: 'Terbatas',
      addAttraction: 'Tambah',
      attractionSelected: 'Dipilih',
      requiredTag: 'Wajib',
      slotSelectedTag: 'Terpilih',
      removeTicket: 'Hapus tiket',
      cashSubtitle: 'Uang kertas IDR',
    },
    wristband: {
      rescan: 'Pindai Ulang',
      connectedBadge: 'Gelang Terhubung',
      yourRides: 'Wahana Anda',
      registeredCount: '{count} terdaftar',
      loadingEntitlements: 'Memuat daftar wahana...',
      noEntitlementsTitle: 'Belum ada wahana terdaftar',
      noEntitlementsHint: 'Tekan "Beli Wahana" di bawah untuk menambahkan',
      whatToBuy: 'Apa yang ingin Anda beli?',
      addonEyebrow: 'Wahana & Addon',
      addonTitle: 'Beli Wahana',
      addonDescription: 'Tambahkan wahana tambahan ke kunjungan Anda hari ini',
      transferEyebrow: 'Bagi Wahana',
      transferTileTitle: 'Transfer Add-on',
      transferTileDescription: 'Pindahkan wahana ke orang lain dalam pemesanan yang sama',
      transferSelectTitle: 'Pilih Wahana untuk Ditransfer',
      transferLoading: 'Memuat wahana...',
      transferLoadFailed: 'Gagal memuat wahana. Coba lagi.',
      transferRetry: 'Coba Lagi',
      transferNoneTitle: 'Tidak Ada Wahana untuk Ditransfer',
      transferNoneHint: 'Hanya wahana aktif untuk hari ini yang bisa ditransfer.',
      transferSelectedCount: '{count} wahana dipilih',
      transferNext: 'Lanjut',
      transferScanTitle: 'Scan Gelang Penerima',
      transferScanSubtitle: 'Scan gelang orang yang akan menerima wahana',
      transferResolving: 'Memverifikasi gelang...',
      transferRecipientFoundTitle: 'Penerima Ditemukan',
      transferContinueWithRecipient: 'Lanjut ke Konfirmasi',
      transferScanAnother: 'Scan Gelang Lain',
      transferErrorTitle: 'Tidak Dapat Melanjutkan',
      transferConfirmTitle: 'Konfirmasi Transfer',
      transferFrom: 'Dari',
      transferTo: 'Ke',
      transferItemsLabel: 'Wahana yang Ditransfer',
      transferWarnOnce:
        'Transfer ini tidak bisa dibatalkan. Wahana langsung berpindah ke gelang penerima.',
      transferSubmit: 'Transfer Sekarang',
      transferSending: 'Memproses transfer...',
      transferFailed: 'Transfer gagal. Coba lagi.',
      transferCannotSendToSelf: 'Tidak bisa transfer ke gelang sendiri.',
      transferDifferentOrder:
        'Gelang ini milik pemesanan lain. Transfer hanya bisa ke sesama pemesanan yang sama.',
      transferRecipientNotFound: 'Gelang penerima tidak ditemukan.',
      transferNoCustomer: 'Gelang tidak terhubung ke akun. Minta bantuan petugas.',
      transferTicketsUnavailable:
        'Beberapa wahana sudah tidak tersedia. Kembali dan pilih ulang.',
      transferInvalidCode: 'Kode gelang tidak valid.',
      transferSuccessHeadline: 'Transfer Berhasil!',
      transferSuccessMessage: 'Wahana sudah dipindahkan ke gelang {name}.',
      transferNumberLabel: 'Nomor Transfer',
      transferItemsCount: '{count} wahana ditransfer',
      ctaStart: 'Mulai',
      footerHint:
        'Bukan gelang Anda? Tekan "Pindai Ulang" di atas. Butuh bantuan? Hubungi staf.',
      backToProfile: 'Kembali ke Profil',
      backToWristbandProfile: 'Kembali ke Profil Gelang',
      fnbEyebrow: 'Kuliner',
      fnbDescription: 'Pesan makanan untuk diambil di outlet F&B',
      addonPickerBadge: 'Beli Wahana',
      addonPickerTitle: 'Pilih Wahana',
      addonPickerForWristband: 'Untuk gelang',
      visitLabel: 'Kunjungan',
      loadAttractionsFailed: 'Gagal memuat daftar wahana',
      noAttractionsAvailableTitle: 'Belum ada wahana yang tersedia',
      noAttractionsAvailableHint: 'Silakan coba lagi nanti atau tanyakan ke staf',
      slotsAvailable: '{count} slot tersedia',
      selectedCountShort: '{count} dipilih',
      noSlotsToday: 'Tidak ada slot tersedia hari ini',
      limitedSlot: 'Terbatas',
      ridesSelectedCount: '{count} wahana dipilih',
      continueToPayment: 'Lanjut ke Pembayaran',
      addonSuccessHeadline: 'Wahana Ditambahkan ke Gelang',
      addonSuccessEnjoy: 'Nikmati kunjungan Anda, {name}!',
      addonSuccessRecorded:
        'Wahana baru sudah tercatat di gelang {wristbandId} dan dapat langsung digunakan di gerbang wahana.',
      guestFallback: 'Pengunjung',
      addonTransactionLabel: 'No. Transaksi',
      entitlementsLoadFailed: 'Gagal memuat daftar wahana',
      // ── #461 C3: wristband screens + components ──
      scanServiceEyebrow: 'Layanan Gelang',
      scanEyebrow: 'Gelang Anda',
      scanTitle: 'Tempelkan Gelang',
      scanSubtitle: 'Dekatkan gelang ke pemindai — atau ketik kode di bawah',
      scanProcessing: 'Memproses gelang...',
      scanInputLabel: 'atau ketik kode gelang',
      scanInputPlaceholder: 'Kode gelang...',
      scanNotRecognizedTitle: 'Gelang Tidak Dikenali',
      scanNotRecognizedError: 'Gelang tidak dikenali. Pastikan gelang Anda masih aktif.',
      scanNoWristbandPrompt: 'Belum punya gelang?',
      scanBuyNewTicket: 'Beli tiket baru',
      paymentTimeoutError: 'Pembayaran melebihi batas waktu. Silakan coba lagi.',
      paymentFailedWithReason: 'Pembayaran gagal. ({reason})',
      paymentCancelledError: 'Pembayaran dibatalkan. Silakan coba lagi.',
      paymentFailedRetry: 'Pembayaran gagal. Silakan coba lagi.',
      qrisConfigIncomplete: 'Konfigurasi pembayaran QRIS tidak lengkap.',
      waitingPaymentStatus: 'Menunggu Pembayaran',
      processingStatus: 'Memproses',
      verifyingPayment: 'Memverifikasi pembayaran...',
      continuePaymentInWindow: 'Lanjutkan pembayaran di jendela yang terbuka',
      doNotClosePage: 'Jangan tutup halaman ini sampai transaksi selesai.',
      changeSelection: 'Ubah Pilihan',
      paymentSummary: 'Ringkasan Pembayaran',
      totalPayment: 'Total Pembayaran',
      ridesCountLabel: '{count} wahana',
      selectedRides: 'Wahana yang Dipilih',
      slotTimePrice: '{time} WIB · {price}',
      removeFromCart: 'Hapus dari keranjang',
      paymentFailedTitle: 'Pembayaran Gagal',
      total: 'Total',
      payAmount: 'Bayar {amount}',
      viaQris: 'via QRIS',
      greetingEyebrow: 'Selamat Datang',
      greetingHello: 'Halo, {name}',
      statusActive: 'Aktif',
      entryTicketHeader: 'Tiket Masuk',
      entryCategoryFallback: 'ENTRY',
      visitDateLabel: 'Tanggal kunjungan:',
      entitlementStatusActive: 'Belum Digunakan',
      entitlementStatusUsed: 'Sudah Dipakai',
      entitlementStatusExpired: 'Kedaluwarsa',
      attractionFallback: 'Wahana',
      usedAtLine: 'Digunakan {datetime}',
      expiredAtLine: 'Kedaluwarsa {datetime}',
      validUntilLine: 'Berlaku sampai {datetime}',
    },
    staffReprint: {
      accessBadge: 'Akses Staf',
      title: 'Cetak Ulang Wristband',
      reasonLabel: 'Alasan Cetak Ulang',
      reasonLost: 'Hilang',
      reasonDamaged: 'Rusak / Sobek',
      reasonNotReceived: 'Tidak Diterima',
      reasonPrintFailure: 'Gagal Cetak Sebelumnya',
      reasonOther: 'Lainnya',
      submitProcessing: 'Memproses…',
      submitButton: '🖨️ Cetak Ulang',
      pinErrorGeneric: 'PIN salah atau terjadi kesalahan',
      keypadDelete: 'Hapus',
      keypadClear: 'Bersihkan',
      keypadDigit: 'Angka {digit}',
    },
  },
  en: {
    welcome: {
      title: 'Welcome to',
      subtitle: 'Autopia Theme Park',
      scanWristband: 'Scan Wristband',
      buyTickets: 'Buy Tickets',
      tagline: 'Touch to get started',
      museumParks: 'Museum & Parks',
      touchToStart: 'Touch to Start',
      processingTicket: 'Processing ticket…',
      qrPrintHint: 'Scan your ticket QR to print your wristband',
      onlineTicketLabel: 'Online ticket',
      ordGrpBookingHint: 'Already have an ORD- or GRP- booking?',
      walkInLabel: 'Walk-in',
      walkInBuyHint: 'Choose tickets & rides here',
      selectLanguagePrompt: 'Please select your language',
      wristbandBuyAddonsSubtitle: 'Buy rides & add-ons',
      backToMainScreen: '← Back to main screen',
    },
    scan: {
      title: 'Print Your Online Ticket',
      instruction: 'Scan your booking code, or type the ORD-… / GRP-… code below',
      placeholder: 'Booking code (ORD-… or GRP-…)',
      scanning: 'Processing...',
      orType: 'or type the code',
      submit: 'Print Ticket',
      headerBadge: 'Online ticket printing',
      groupBadge: 'Group',
      submitGroup: 'Print group wristbands',
      individualCardTitle: 'Individual (ORD-)',
      individualCardHint: 'Print wristbands for your online order',
      groupCardTitle: 'Group (GRP-)',
      groupCardHint: 'Print wristbands for every group member at once',
      wristbandUpsell: 'Already have a wristband and want rides or food?',
      unknownCode:
        'Unrecognised code. This screen is for online ticket printing only. To use your wristband, go home and choose "Scan wristband".',
      orderNotFound:
        'Order not found. Check the code spelling or ask a staff member for help.',
      pickupUnavailable: 'Ticket pickup is temporarily unavailable',
      printUnavailable: 'Printing is temporarily unavailable',
      printFailedGeneric: 'Print failed',
      // ── #461 C4: pickup confirmation + printing ──
      pickupConfirmBadge: 'Confirm Order',
      pickupTitle: 'Print Your Wristbands',
      pickupOrderLabel: 'Order',
      pickupNameLabel: 'Name',
      pickupEntryTickets: 'Entry Tickets',
      pickupAddons: 'Additional Attractions',
      pickupAlreadyPickedUpHint: 'This order has already been picked up. Contact staff if you need a reprint.',
      pickupPrintButton: 'Print {count} Wristbands',
      printingGroupTitle: 'Printing group wristbands',
      printingWait: 'Please wait a moment...',
    },
    printResult: {
      titleSuccess: 'Wristbands printed!',
      titlePartial: 'Some prints failed',
      titleAlreadyPrinted: 'Already printed',
      alreadyPrintedBadge: 'Previously printed',
      timeUnknown: 'Time unknown',
      alreadyPrintedHint:
        '{count} wristbands for this group were already printed. Contact staff if you need a reprint.',
      bookingLabel: 'Booking',
      totalColumn: 'Total',
      backHome: 'Back to home',
    },
    quickPickup: {
      welcomeHeading: 'Welcome!',
      guestNameFallback: 'Guest',
      alreadyPrintedBody: 'Your wristbands were already printed.',
      alreadyPrintedHint: 'Talk to a staff member if something looks wrong.',
      printingBody: 'Your wristbands are printing.',
      printingHint: 'Please wait a moment.',
      backToMainMenu: 'Back to main menu',
      autoReturnLead: 'Returning automatically in',
      autoReturnSuffix: 'seconds…',
    },
    balance: {
      title: 'Account Information',
      greeting: 'Hello',
      creditBalance: 'Credit Balance',
      tier: 'Tier',
      topUp: 'Top Up Credits',
      history: 'Check History',
      done: 'Done',
    },
    topUp: {
      title: 'Top Up Credits',
      selectAmount: 'Select Amount',
      customAmount: 'Custom Amount',
      paymentMethod: 'Payment Method',
      cash: 'Cash',
      qris: 'QRIS',
      edcCard: 'EDC Card',
      total: 'Total',
      continue: 'Continue',
      back: 'Back',
      minAmount: 'Minimum top-up amount is Rp 10,000',
      insertCash: 'Please insert cash into the machine',
      confirmReceived: 'Confirm Payment Received',
      scanQris: 'Scan QR code to pay',
      waitingPayment: 'Waiting for payment...',
      qrisInstruction: 'Open your digital wallet app and scan the QR code above',
      tapCard: 'Please tap or insert your card',
      waitingCard: 'Waiting for card...',
    },
    purchase: {
      title: 'Buy Attraction Tickets',
      attractionCredits: 'Attraction Tickets (Credits)',
      addToCart: 'Add',
      cart: 'Cart',
      totalCredits: 'Total Credits',
      checkout: 'Pay Now',
      emptyCart: 'Cart is empty',
      scanFirst: 'Scan wristband first to checkout',
      remove: 'Remove',
      credits: 'credits',
    },
    error: {
      title: 'Something Went Wrong',
      defaultMessage: 'We apologize for the inconvenience. Please try again or call staff.',
      tryAgain: 'Try Again',
      callStaff: 'Call Staff',
      callingStaff: 'Calling staff...',
      boundaryTitle: 'Sorry, something went wrong',
      boundaryMessage:
        'The kiosk hit an unexpected problem. Please return to the home screen or ask a staff member if this keeps happening.',
      boundaryBackHome: 'Back to home',
    },
    success: {
      title: 'Success!',
      receiptDetails: 'Transaction Details',
      printReceipt: 'Print Receipt',
      done: 'Done',
      returningIn: 'Returning to main screen in',
      seconds: 'seconds',
      transactionId: 'Transaction ID',
      time: 'Time',
      notes: 'Description',
    },
    history: {
      title: 'Transaction History',
      noHistory: 'No transaction history yet',
      back: 'Back',
      date: 'Date',
      description: 'Description',
      amount: 'Amount',
    },
    inactivity: {
      warning: 'Are you still there?',
      countdown: 'Session will end in',
      stayHere: 'Yes, I\'m Still Here',
      secondsSuffix: 's',
    },
    common: {
      loading: 'Loading...',
      back: 'Back',
      cancel: 'Cancel',
      confirm: 'Confirm',
      close: 'Close',
      currency: 'Rp',
      processing: 'Processing...',
      success: 'Success',
      failed: 'Failed',
      error: 'An error occurred while loading data',
      logout: 'Logout',
      homeLabel: 'Home',
      homeAriaLabel: 'Back to home',
      currentTimeAriaLabel: 'Current time',
    },
    language: {
      toggle: 'ID',
      id: '🇮🇩 Indonesia',
      en: '🇬🇧 English',
    },
    tiers: {
      BRONZE: 'Bronze',
      SILVER: 'Silver',
      GOLD: 'Gold',
      PLATINUM: 'Platinum',
    },
    info: {
      home: 'Park Information',
      parkMap: 'Park Map',
      waitTimes: 'Wait Times',
      showSchedule: 'Show Schedule',
      myTickets: 'My Tickets',
      nowShowing: 'Now Showing',
      estimatedWait: 'Estimated Wait',
      noShows: 'No shows scheduled today',
      creditsRemaining: 'Credits Remaining',
      minHeight: 'Min. height',
      queueDepth: 'Queue',
      noWaitData: 'Data unavailable',
      scanWristband: 'Scan your wristband to view your tickets',
      wristbandInfo: 'Wristband Info',
      attractionsPurchased: 'Attractions Purchased',
      reservationsToday: 'Reservations Today',
      noReservations: 'No reservations today',
      backToInfo: 'Back to Info',
      autoReturn: 'Auto-returning in',
      refreshIn: 'Refreshing in',
    },
    photo: {
      gallery: 'Photo Gallery',
      selectPhotos: 'Select Photos',
      cart: 'Photo Cart',
      purchasePhotos: 'Purchase Photos',
      downloadLink: 'Download Link',
      receipt: 'Purchase Receipt',
      pricePerPhoto: 'Price per photo',
      totalPhotos: 'Total photos',
      payWithCredits: 'Pay with Wristband Credits',
      payWithCard: 'Pay with Card',
      backToGallery: 'Back to Gallery',
      noPhotos: 'No photos taken today',
      scanWristband: 'Scan your wristband to view your photos',
      selected: 'selected',
      viewSelected: 'View Selected ({count})',
      downloadSent: 'Download link sent to your email/WhatsApp',
      newTransaction: 'New Transaction',
      capturedAt: 'Captured at',
    },
    fnb: {
      menuTitle: 'Food & Drinks',
      welcomeSubtitle: 'Food & drinks',
      cartTitle: 'Cart',
      allergenFilter: 'Allergen Filter',
      allergenFilterActive: 'Filter Active',
      addToCart: 'Add to Cart',
      viewCart: 'View Cart',
      placeOrder: 'Order Now',
      orderNumber: 'Order Number',
      estimatedReady: 'Ready in',
      minutes: 'minutes',
      orderReady: 'Your Order is Ready!',
      soldOut: 'Sold Out',
      payWristband: 'Pay with Wristband',
      payCard: 'Card / E-Wallet',
      proceedCashier: 'Proceed to Cashier',
      orderBeingPrepared: 'Your order is being prepared',
      backToMenu: 'Back to Menu',
      done: 'Done',
      emptyCart: 'Your cart is empty',
      menuUnavailable: 'Menu not available',
      backToMenuBtn: 'Back to Menu',
      subtotal: 'Subtotal',
      orderItems: 'Order',
      total: 'Total',
      allergenWarning: 'Warning: Order contains allergens you have flagged',
      orderStatusTitle: 'Order Status',
      enterOrderRef: 'Enter order number',
      checkStatus: 'Check Status',
      qty: 'Qty',
      remove: 'Remove',
      selectVariant: 'Select Variant',
      selectModifiers: 'Add-ons (optional)',
      quantity: 'Quantity',
      sufficient: 'Sufficient balance',
      insufficient: 'Insufficient balance',
      paymentMethod: 'Payment Method',
      orderPlacing: 'Placing order...',
      statusLookup: 'Order Status Lookup',
      // ── #461 C5 ──
      itemsUnit: 'item',
      quantityForItem: 'Quantity for {name}',
      decreaseQuantity: 'Decrease quantity of {name}',
      increaseQuantity: 'Increase quantity of {name}',
      orderSummaryLabel: 'Order summary',
      menuCategoriesLabel: 'Menu categories',
      cartSummaryLabel: 'Cart summary',
    },
    login: {
      title: 'Login',
      submit: 'Login',
      loggingIn: 'Logging in...',
      emailLabel: 'Email',
      passwordLabel: 'Password',
      emailPlaceholder: 'Enter your email',
      passwordPlaceholder: 'Enter your password',
      kioskOnlyError: 'This app is for kiosk accounts only. Please use an account with the KIOSK role.',
    },
    validation: {
      required: 'This field is required',
      invalidType: 'Invalid value',
      tooSmall: 'Value is too small',
      tooBig: 'Value is too large',
      email: 'Enter a valid email address',
      invalidFormat: 'Invalid format',
    },
    walkUp: {
      stepTicket: 'Entry Ticket',
      stepAttractions: 'Attractions',
      stepSummary: 'Summary',
      stepIndicator: 'Step {step} / 3',
      catalogTitle: 'Select Tickets',
      selectDate: 'Select Visit Date',
      today: 'Today',
      tomorrow: 'Tomorrow',
      soldOut: 'Sold Out',
      attractionsTitle: 'Add Attraction Tickets',
      attractionsSubtitle: 'Optional — skip if you don\'t want to add attractions',
      skip: 'Skip',
      continue: 'Continue',
      selectSchedule: 'Choose times',
      attractionsPanelTitle: 'Add rides',
      attractionsPanelSubtitle: 'Pick rides for each of your tickets',
      emptyAttractionsHint: 'Please select entry tickets first',
      ticketCountLabel: '{count} tickets',
      attractionSkip: 'Skip, go to cart',
      cartTitle: 'Your Cart',
      cartEmpty: 'Your cart is empty',
      subtotal: 'Subtotal',
      total: 'Total',
      continuePayment: 'Continue to Payment',
      paymentTitle: 'Select Payment Method',
      methodQris: 'QRIS',
      methodEdc: 'Card (EDC)',
      methodCash: 'Cash',
      qrisInstruction: 'Scan the QR code with your payment app',
      qrisTitle: 'Scan QR with your e-wallet',
      qrisExpiresIn: 'Expires in',
      qrisLoading: 'Loading QR…',
      edcInsertCard: 'Insert or tap your card on the EDC terminal',
      edcProcessing: 'Processing payment...',
      edcApproved: 'Payment Approved',
      edcDeclined: 'Payment Declined',
      cashInsertCash: 'Insert cash into the machine',
      paymentTimeout: 'Payment timed out',
      paymentRetry: 'Try Again',
      successTitle: 'Payment Successful',
      printingReceipt: 'Printing receipt...',
      takeReceipt: 'Please take your receipt',
      goToGate: 'Proceed to the Entry Gate for Wristband Activation',
      orderNumberLabel: 'Order Number',
      bookingNumberLabel: 'Booking Number',
      staffAssistTitle: 'Please Call Staff',
      staffAssistMessage: 'Show the transaction number below to staff',
      networkError: 'Connection lost. Please try again.',
      // Identity collection
      identityTitle: 'Your Details',
      identitySubtitle: 'Enter your name and email to receive your e-ticket',
      customerName: 'Full Name',
      customerEmail: 'Email',
      continueToTickets: 'Continue to Tickets',
      catalogLoadFailed: 'Could not load rides',
      paymentFailedWithReason: 'Payment failed. ({reason})',
      paymentCancelledRetry: 'Payment cancelled. Please try again.',
      paymentFailedRetry: 'Payment failed. Please try again.',
      qrisSnapIncomplete: 'QRIS payment configuration is incomplete.',
      paymentProgressBadgeWaiting: 'Waiting for payment',
      paymentProgressBadgeConfirming: 'Processing payment',
      paymentProgressTitleWaiting: 'Complete your payment',
      paymentProgressTitleConfirming: 'Processing payment',
      paymentProgressSubtitleWaiting: 'Follow the on-screen instructions to finish',
      paymentProgressSubtitleConfirming: 'Please wait — we are verifying your payment',
      paymentQrisPending: 'Payment detected, verifying transaction...',
      paymentQrisOpenSnap: 'Continue payment in the Midtrans window that opened',
      paymentQrisKeepOpen: 'Do not leave this page until verification finishes.',
      paymentConfirmingLine: 'Processing payment',
      paymentConfirmingWait: 'Verifying your transaction, please wait...',
      xenditOverlayTitle: 'Secure Payment',
      xenditOverlayCancel: 'Cancel',
      xenditOverlayLoading: 'Loading payment page...',
      xenditOverlayEmbedError: 'Payment page could not be loaded here',
      xenditOverlayOpenExternal: 'Open Payment Page',
      xenditOverlayFooter: 'Complete payment here. This overlay will close automatically once payment is confirmed.',
      // ── #461 C1: walk-up screens ──
      entryTitle: 'Select Entry Ticket',
      entrySubtitle: 'Choose the entry ticket type or bundle you want',
      cancel: 'Cancel',
      bundleBadge: 'Bundle',
      viewBundleContents: 'View bundle contents',
      bundleSavings: 'Save {percent}%',
      decrease: 'Decrease',
      increase: 'Add',
      addItem: 'Add {name}',
      noTicketsToday: 'No tickets available for today',
      tabIndividual: 'Individual Tickets',
      tabBundle: 'Bundle Packages',
      noIndividualTickets: 'No individual tickets available',
      noBundlesAvailable: 'No bundles available',
      selectPaymentMethod: 'Select payment method',
      selectEdcBank: 'Choose EDC bank',
      payAmount: 'Pay {amount}',
      viaEdc: 'via EDC · {bank}',
      viaQris: 'via QRIS',
      viaCash: 'via Cash',
      startOrder: 'Start ordering tickets',
      overviewTitle: 'Summary & Payment',
      overviewSubtitleAction: 'Choose a payment method to finish',
      attractionCountLabel: '{count} attractions',
      totalPayment: 'Total Payment',
      vatIncluded: 'VAT 11% included',
      paymentMethodTitle: 'Payment Method',
      paymentFailed: 'Payment Failed',
      orderSummary: 'Order Summary',
      edit: 'Edit',
      visitDateLabel: 'Visit Date',
      ticketsLabel: 'Tickets',
      ticketsReady: '{count} tickets ready',
      preparing: 'Preparing…',
      wristbandsHeading: 'Your Wristbands',
      wristbandN: 'Wristband {n}',
      nameLabel: 'Name',
      printerUnavailableShowQr: 'Printer unavailable — show this QR at the gate',
      printFailedShowQr: 'Print failed — show this QR at the gate',
      qrWristbandAlt: 'QR for wristband {code}',
      takeWristbandsEnjoy: 'Take your wristbands and enjoy the park!',
      preparingWristbands: 'Preparing wristbands…',
      retryPrint: 'Retry Print',
      ticketsProcessingShowReceipt: 'Tickets are being processed. Show your receipt to staff at the gate.',
      preparingTickets: 'Preparing tickets…',
      backToHome: 'Back to Home',
      // ── #461 C2: walk-up components ──
      ticketNumberLabel: 'Ticket #{number}',
      needsScheduleTag: 'Needs Schedule',
      bundleIncludes: 'Bundle Includes',
      bundleAttractionsForVisitor: 'Bundle Attractions — Pick a Time',
      bundleIncluded: 'Included in bundle',
      bundleRedemptions: 'Included in bundle · {count} rides',
      selectAddonAttractions: 'Select Add-on Attractions',
      applyToAll: 'Apply to all',
      noAttractionsAvailable: 'No attractions available',
      noSlotsAvailable: 'No time slots available',
      limited: 'Limited',
      addAttraction: 'Add',
      attractionSelected: 'Added',
      requiredTag: 'Required',
      slotSelectedTag: 'Selected',
      removeTicket: 'Remove ticket',
      cashSubtitle: 'IDR banknotes',
    },
    wristband: {
      rescan: 'Rescan',
      connectedBadge: 'Wristband connected',
      yourRides: 'Your rides',
      registeredCount: '{count} registered',
      loadingEntitlements: 'Loading your rides…',
      noEntitlementsTitle: 'No rides on file yet',
      noEntitlementsHint: 'Tap "Buy rides" below to add some',
      whatToBuy: 'What would you like to buy?',
      addonEyebrow: 'Rides & add-ons',
      addonTitle: 'Buy rides',
      addonDescription: 'Add more rides to today’s visit',
      transferEyebrow: 'Share rides',
      transferTileTitle: 'Transfer Add-on',
      transferTileDescription: 'Move rides to someone else on the same booking',
      transferSelectTitle: 'Select Rides to Transfer',
      transferLoading: 'Loading rides...',
      transferLoadFailed: 'Failed to load rides. Please try again.',
      transferRetry: 'Try Again',
      transferNoneTitle: 'No Rides Available to Transfer',
      transferNoneHint: "Only today's active rides can be transferred.",
      transferSelectedCount: '{count} ride(s) selected',
      transferNext: 'Next',
      transferScanTitle: 'Scan Recipient Wristband',
      transferScanSubtitle: 'Scan the wristband of the person who will receive the rides',
      transferResolving: 'Verifying wristband...',
      transferRecipientFoundTitle: 'Recipient Found',
      transferContinueWithRecipient: 'Continue to Confirm',
      transferScanAnother: 'Scan Another Wristband',
      transferErrorTitle: 'Cannot Continue',
      transferConfirmTitle: 'Confirm Transfer',
      transferFrom: 'From',
      transferTo: 'To',
      transferItemsLabel: 'Rides Being Transferred',
      transferWarnOnce:
        'This cannot be undone. The rides move to their wristband immediately.',
      transferSubmit: 'Transfer Now',
      transferSending: 'Processing transfer...',
      transferFailed: 'Transfer failed. Please try again.',
      transferCannotSendToSelf: 'You cannot transfer to your own wristband.',
      transferDifferentOrder:
        'This wristband belongs to a different booking. Transfers are only allowed within the same booking.',
      transferRecipientNotFound: 'Recipient wristband not found.',
      transferNoCustomer: 'Wristband is not linked to an account. Please ask for staff assistance.',
      transferTicketsUnavailable:
        'Some rides are no longer available. Go back and reselect.',
      transferInvalidCode: 'Invalid wristband code.',
      transferSuccessHeadline: 'Transfer Successful!',
      transferSuccessMessage: 'Rides have been moved to {name}\'s wristband.',
      transferNumberLabel: 'Transfer Number',
      transferItemsCount: '{count} ride(s) transferred',
      ctaStart: 'Start',
      footerHint:
        'Not your wristband? Use "Rescan" at the top. Need help? Ask a staff member.',
      backToProfile: 'Back to profile',
      backToWristbandProfile: 'Back to wristband profile',
      fnbEyebrow: 'Food',
      fnbDescription: 'Order food for pickup at an F&B outlet',
      addonPickerBadge: 'Buy rides',
      addonPickerTitle: 'Choose rides',
      addonPickerForWristband: 'For wristband',
      visitLabel: 'Visit',
      loadAttractionsFailed: 'Could not load rides',
      noAttractionsAvailableTitle: 'No rides available right now',
      noAttractionsAvailableHint: 'Please try again later or ask a staff member',
      slotsAvailable: '{count} slots open',
      selectedCountShort: '{count} selected',
      noSlotsToday: 'No time slots available today',
      limitedSlot: 'Limited',
      ridesSelectedCount: '{count} rides selected',
      continueToPayment: 'Continue to payment',
      addonSuccessHeadline: 'Rides added to your wristband',
      addonSuccessEnjoy: 'Enjoy your visit, {name}!',
      addonSuccessRecorded:
        'Your new rides are linked to wristband {wristbandId} and ready to use at ride entrances.',
      guestFallback: 'Guest',
      addonTransactionLabel: 'Transaction no.',
      entitlementsLoadFailed: 'Could not load your rides',
      // ── #461 C3: wristband screens + components ──
      scanServiceEyebrow: 'Wristband Service',
      scanEyebrow: 'Your Wristband',
      scanTitle: 'Tap Your Wristband',
      scanSubtitle: 'Hold your wristband near the scanner — or type the code below',
      scanProcessing: 'Processing wristband...',
      scanInputLabel: 'or type the wristband code',
      scanInputPlaceholder: 'Wristband code...',
      scanNotRecognizedTitle: 'Wristband Not Recognized',
      scanNotRecognizedError: 'Wristband not recognized. Make sure your wristband is still active.',
      scanNoWristbandPrompt: "Don't have a wristband yet?",
      scanBuyNewTicket: 'Buy a new ticket',
      paymentTimeoutError: 'Payment timed out. Please try again.',
      paymentFailedWithReason: 'Payment failed. ({reason})',
      paymentCancelledError: 'Payment cancelled. Please try again.',
      paymentFailedRetry: 'Payment failed. Please try again.',
      qrisConfigIncomplete: 'QRIS payment configuration is incomplete.',
      waitingPaymentStatus: 'Awaiting Payment',
      processingStatus: 'Processing',
      verifyingPayment: 'Verifying payment...',
      continuePaymentInWindow: 'Continue payment in the open window',
      doNotClosePage: 'Do not close this page until the transaction is complete.',
      changeSelection: 'Change Selection',
      paymentSummary: 'Payment Summary',
      totalPayment: 'Total Payment',
      ridesCountLabel: '{count} rides',
      selectedRides: 'Selected Rides',
      slotTimePrice: '{time} WIB · {price}',
      removeFromCart: 'Remove from cart',
      paymentFailedTitle: 'Payment Failed',
      total: 'Total',
      payAmount: 'Pay {amount}',
      viaQris: 'via QRIS',
      greetingEyebrow: 'Welcome',
      greetingHello: 'Hi, {name}',
      statusActive: 'Active',
      entryTicketHeader: 'Entry Ticket',
      entryCategoryFallback: 'ENTRY',
      visitDateLabel: 'Visit date:',
      entitlementStatusActive: 'Not Used Yet',
      entitlementStatusUsed: 'Used',
      entitlementStatusExpired: 'Expired',
      attractionFallback: 'Attraction',
      usedAtLine: 'Used {datetime}',
      expiredAtLine: 'Expired {datetime}',
      validUntilLine: 'Valid until {datetime}',
    },
    staffReprint: {
      accessBadge: 'Staff access',
      title: 'Reprint wristbands',
      reasonLabel: 'Reprint reason',
      reasonLost: 'Lost',
      reasonDamaged: 'Damaged / torn',
      reasonNotReceived: 'Not received',
      reasonPrintFailure: 'Previous print failed',
      reasonOther: 'Other',
      submitProcessing: 'Processing…',
      submitButton: '🖨️ Reprint',
      pinErrorGeneric: 'Incorrect PIN or something went wrong',
      keypadDelete: 'Delete',
      keypadClear: 'Clear',
      keypadDigit: 'Digit {digit}',
    },
  },
};
